import "./AddToBasketControl.scss";

import * as React from "react";

import PropTypes from "prop-types";
import { Button, Popover, OverlayTrigger } from "react-bootstrap";
import ReactCSSTransitionGroup from "react-addons-css-transition-group";
import { FormattedMessage } from "react-intl";
import { AmountChooser } from "./AmountChooser";
import { PriceAndPromotionInfo } from "shared/src/types/PriceAndPromotionInfo";
import { getComponentByName } from "shared/src/components/LazyWrapper";
import { calculateAmountToDisplay, canNotBeAddedToBasket } from "shared/src/utils/BasketUtil";
import { InternalDisplayConfig } from "shared/src/types/ArticleCardProps";
import type { BettyBundle, BettyBundleStore } from "shared/src/types/EvaluateArticleNew";
import { FunctionComponent } from "react";
import MoqMessage from "shared/src/components/AddToBasket/MoqMessage";

const AlternativesButtonWithModal = getComponentByName("sd_pdp_ui/AlternativesButtonWithModal");

type AddToBasketControlProps = {
    onAddToCart: (amount: number, bettyBundleId: string | null | undefined) => void;
    selectedBundle: BettyBundle | null;
    priceAndPromotionInfo: PriceAndPromotionInfo | null;
    internalDisplayConfig: InternalDisplayConfig | null;
    selectedStoreData: BettyBundleStore | null;
    amountInBasket: number | undefined | null;
    articleDeprecated?: boolean;
    enabled?: boolean;
    tabIndex?: number;
};

type State = {
    articleJustAdded: boolean;
    showWeightInformation: boolean;
    quantityInAmountChooser: number | undefined | null;
    userDidModifyAmount: boolean;
};

type DerivedProps = {
    bettyBundleId: string | undefined | null;
    isWeightArticle: boolean;
    weightPerPiece: {
        unit: string | undefined;
        value: number | undefined;
    } | null;
    onlyEmployeeBuyable?: boolean;
    deliveryMode?: string;
    amountInBasket?: number;
    minOrderQuantity?: number;
    canNotBeAddedToBasket?: boolean;
};

function computeDerivedProps(props: AddToBasketControlProps) {
    return {
        bettyBundleId: props.selectedBundle?.bettyBundleId?.bettyBundleId,
        isWeightArticle: props.selectedBundle?.isWeightArticle === "WEIGHT",
        weightPerPiece: {
            unit: props.selectedBundle?.weightPerPiece?.uom,
            value: props.selectedBundle?.weightPerPiece?.value
        },
        onlyEmployeeBuyable: !props.selectedStoreData?.customerBuyable && props.selectedStoreData?.employeeBuyable,
        deliveryMode: props.selectedStoreData?.selectedDeliveryMode,
        amountInBasket: props.amountInBasket || undefined,
        minOrderQuantity: props.selectedBundle?.minOrderQuantity,
        canNotBeAddedToBasket: canNotBeAddedToBasket(
            !!props.articleDeprecated,
            !props.selectedBundle,
            !!props.enabled,
            props.selectedStoreData
        )
    };
}

export class AddToBasketControl extends React.Component<AddToBasketControlProps, State> {
    static contextTypes = {
        intl: PropTypes.object.isRequired
    };

    derivedProps: DerivedProps = computeDerivedProps(this.props);

    state: State = {
        articleJustAdded: false,
        showWeightInformation: false,
        quantityInAmountChooser: this.getAmountChooserDefaultValue(),
        userDidModifyAmount: false
    };

    componentWillReceiveProps(nextProps: AddToBasketControlProps) {
        const latestBundleId = nextProps.selectedBundle?.bettyBundleId?.bettyBundleId;
        const previousBundleId = this.derivedProps.bettyBundleId;
        this.derivedProps = computeDerivedProps(nextProps);
        if (latestBundleId !== previousBundleId) {
            // New bundle selected, back to initial state
            this.setState({
                quantityInAmountChooser: this.getAmountChooserDefaultValue(),
                userDidModifyAmount: false
            });
        } else {
            // Still the same bundle selected, thus respect user changes
            this.setState({
                quantityInAmountChooser: this.state.userDidModifyAmount
                    ? this.state.quantityInAmountChooser
                    : this.getAmountChooserDefaultValue()
            });
        }
    }

    getAmountChooserDefaultValue() {
        const basket = this.derivedProps.amountInBasket || 0;
        const minOrderQuantity = this.derivedProps.minOrderQuantity || 0;
        if (minOrderQuantity > basket) {
            return minOrderQuantity - basket;
        }
        return 1;
    }

    amountInBasketAndAmountChooserLowerThanMOQ() {
        const amountInChooser = this.getAmountInChooser();
        if (!this.derivedProps.minOrderQuantity) {
            return false;
        }
        return this.derivedProps.minOrderQuantity > amountInChooser! + (this.derivedProps.amountInBasket || 0);
    }

    addChosenAmountToBasket(event: React.SyntheticEvent<any> | undefined | null) {
        // If the added quantity is 1, there is no risk of undefined behaviour and the user is allowed to
        // click on the add to cart button several times.
        if (this.state.articleJustAdded && this.state.quantityInAmountChooser !== 1) {
            return;
        }
        // If the minumum order quantity is enabled and the amount is lower than the limit then don't add the value
        if (this.amountInBasketAndAmountChooserLowerThanMOQ()) {
            return;
        }
        if (event) {
            event.preventDefault();
        }

        this.setState({ articleJustAdded: true, showWeightInformation: true });
        // Reset state after some time
        window.setTimeout(
            () =>
                this.setState({
                    articleJustAdded: false,
                    quantityInAmountChooser: this.getAmountChooserDefaultValue()
                }),
            2000
        );
        window.setTimeout(() => {
            if (!this.state.articleJustAdded) {
                this.setState({ showWeightInformation: false });
            }
        }, 5000);

        const amount = this.state.quantityInAmountChooser || 0;

        this.props.onAddToCart(amount, this.derivedProps.bettyBundleId);
    }

    handleAmountUpdate = (amount: number | undefined | null) => {
        this.setState({ userDidModifyAmount: true, quantityInAmountChooser: amount });
    };

    handleEnterPress = () => {
        this.addChosenAmountToBasket(undefined);
    };

    getAmountInChooser() {
        return this.state.userDidModifyAmount
            ? this.state.quantityInAmountChooser
            : this.getAmountChooserDefaultValue();
    }

    renderMinOrderQuantityPanel() {
        return (
            <MoqMessage
                minOrderQuantity={this.derivedProps.minOrderQuantity}
                isWeightArticle={this.derivedProps.isWeightArticle}
                displayPopover={this.amountInBasketAndAmountChooserLowerThanMOQ()}
            />
        );
    }

    renderAddToBasketButton() {
        const isAnonymousOrTier12Customer =
            !this.props.internalDisplayConfig?.isLoggedIn ||
            this.props.internalDisplayConfig.customerTier === "1" ||
            this.props.internalDisplayConfig.customerTier === "2";
        if (this.derivedProps.deliveryMode === "STORE") {
            const overlay = (
                <Popover id={"only-in-store-message-" + this.derivedProps.bettyBundleId}>
                    <FormattedMessage id="EX_POPOVER_ONLY_BUYABLE_IN_STORE_MESSAGE" />
                </Popover>
            );
            return (
                <div className="sd-add-to-basket-control--actions-onlyinstore">
                    <OverlayTrigger trigger="hover" placement="top" overlay={overlay}>
                        <Button block>
                            <i className="icon-location icon-lg" />
                            <FormattedMessage id="EX_ONLY_AVAILABLE_IN_STORE_MESSAGE" />
                        </Button>
                    </OverlayTrigger>
                </div>
            );
        } else if (
            this.derivedProps.deliveryMode === "PICKUP" &&
            this.derivedProps.canNotBeAddedToBasket &&
            isAnonymousOrTier12Customer
        ) {
            const overlay = (
                <Popover id={"pickup-message-" + this.derivedProps.bettyBundleId}>
                    <FormattedMessage id="EX_POPOVER_PICKUP_MESSAGE" />
                </Popover>
            );
            return (
                <div className="sd-add-to-basket-control--actions-onlyinstore">
                    <OverlayTrigger trigger="hover" placement="top" overlay={overlay}>
                        <Button block>
                            <i className="icon-info icon-lg" />
                            <FormattedMessage id="EX_PICKUP_BUTTON_TEXT" />
                        </Button>
                    </OverlayTrigger>
                </div>
            );
        } else if (this.derivedProps.canNotBeAddedToBasket) {
            return this.renderAlternativesButton(() => (
                <Button
                    block
                    disabled
                    className="pointer-events-none test-disabled-cart-button"
                    aria-label={this.context.intl.formatMessage({
                        id: "SD_ADD_TO_BASKET_BUTTON_ARIA_LABEL"
                    })}>
                    <i key="shoppingCartIconCart" className="icon-lg icon-cart no-margin" />
                </Button>
            ));
        }
        const icon = this.state.articleJustAdded ? (
            <i key="shoppingCartIconCheck" className="icon-lg icon-check" />
        ) : (
            <i key="shoppingCartIconCart" className="icon-lg icon-cart" />
        );
        const shouldDisableAddButton = this.amountInBasketAndAmountChooserLowerThanMOQ();
        const className =
            "ex-add-to-basket-component add-to-basket-btn " +
            (this.derivedProps.onlyEmployeeBuyable
                ? " employee-only"
                : "" + (shouldDisableAddButton ? " pointer-events-none" : ""));
        return (
            <div
                className={
                    "sd-add-to-basket-control--actions-addtocart " +
                    (this.props.internalDisplayConfig?.isTouchScreen ? "touch-optimized " : "")
                }>
                <Button
                    onClick={(event) => {
                        this.addChosenAmountToBasket(event);
                    }}
                    bsStyle={this.derivedProps.onlyEmployeeBuyable ? "warning" : "primary"}
                    block
                    disabled={shouldDisableAddButton}
                    className={className}
                    aria-label={this.context.intl.formatMessage({
                        id: "SD_ADD_TO_BASKET_BUTTON_ARIA_LABEL"
                    })}>
                    <ReactCSSTransitionGroup
                        transitionName="animation-slideFromBottom"
                        transitionAppear
                        transitionAppearTimeout={0}
                        transitionEnterTimeout={500}
                        transitionLeaveTimeout={500}>
                        {icon}
                    </ReactCSSTransitionGroup>
                    {this.renderAmountInBasket()}
                </Button>
            </div>
        );
    }

    renderAlternativesButton(renderFallback: FunctionComponent | null | undefined) {
        const originalArticle = {
            selectedDeliveryMode: this.derivedProps.deliveryMode,
            price: this.props.priceAndPromotionInfo,
            availability: this.props.selectedBundle?.availability,
            description: this.props.selectedBundle?.description,
            customerDisplayId: this.props.selectedBundle?.customerDisplayId,
            imageUrl: this.props.selectedBundle?.imageUrl,
            bettyBundleId: this.derivedProps.bettyBundleId,
            storeId: this.props.selectedStoreData?.storeId,
            trackingId: this.props.selectedBundle?.trackingInfo?.trackingId
        };
        return (
            <AlternativesButtonWithModal
                originalArticle={originalArticle}
                mainBundleCustomerBuyable={this.props.selectedStoreData?.customerBuyable}
                articleName={this.props.selectedBundle?.description || ""}
                isLoggedIn={this.props.internalDisplayConfig?.isLoggedIn}
                isEmployee={this.props.internalDisplayConfig?.isEmployee}
                isCustomer={this.props.internalDisplayConfig?.isCustomer}
                storeName={this.props.internalDisplayConfig?.storeName}
                pageType={this.props.internalDisplayConfig?.pageType}
                closeAutomatically={false}
                excludedBundleIds={[]}
                alternativesCardType={"BASKET"}
                buttonCssClass={"btn-block test-alternative-button"}
                renderFallback={renderFallback}
            />
        );
    }

    renderAmountInBasket() {
        const amountInBasket = calculateAmountToDisplay(
            this.derivedProps.amountInBasket,
            this.derivedProps.isWeightArticle,
            this.derivedProps.weightPerPiece
        );
        if (!amountInBasket) {
            return null;
        } else {
            const amountToRender = amountInBasket > 999 ? "999+" : amountInBasket;
            return <span className="sd-add-to-basket-control--amount-in-basket badge">{amountToRender}</span>;
        }
    }

    render() {
        const buyable = this.derivedProps.deliveryMode !== "STORE" && !this.derivedProps.canNotBeAddedToBasket;

        return (
            <div className="sd-add-to-basket-control">
                {buyable && (
                    <AmountChooser
                        onChange={this.handleAmountUpdate}
                        onEnter={this.handleEnterPress}
                        internalDisplayConfig={this.props.internalDisplayConfig}
                        priceAndPromotionInfo={this.props.priceAndPromotionInfo}
                        amountInBasket={this.derivedProps.amountInBasket}
                        isWeightArticle={this.derivedProps.isWeightArticle}
                        weightPerPiece={this.derivedProps.weightPerPiece}
                        disabled={!!this.derivedProps.canNotBeAddedToBasket}
                        tabIndex={this.props.tabIndex}
                        amount={this.state.quantityInAmountChooser}
                        showWeightInformation={this.state.showWeightInformation}
                    />
                )}
                {this.renderAddToBasketButton()}
                {this.renderMinOrderQuantityPanel()}
            </div>
        );
    }
}
